import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { 
  Link2, Plus, Search, Edit, Building2, BookOpen, ArrowLeft,
  Wallet, Receipt, CreditCard, Landmark, DollarSign, Check, X
} from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { payrollAPI, companyAPI, financeAPI } from '../../../services/api';
import { toast } from 'sonner';

const GLMapping = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('glmapping');
  const [viewItem, setViewItem] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Dropdown data
  const [companies, setCompanies] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    mapping_id: '',
    name: '',
    description: '',
    company_code_id: '',
    chart_of_accounts_id: '',
    wages_expense_account: '',
    overtime_expense_account: '',
    bonus_expense_account: '',
    allowance_expense_account: '',
    employer_ss_expense_account: '',
    tax_liability_account: '',
    ss_liability_account: '',
    net_pay_clearing_account: '',
    deductions_liability_account: '',
    payroll_accrual_account: '',
    use_cost_center: true,
    default_cost_center: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: '',
    status: 'Active'
  });

  useEffect(() => {
    loadData();
    loadDropdowns();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await payrollAPI.getGLMappings();
      setMappings(response.data || []);
    } catch (error) {
      console.error('Failed to load GL mappings:', error);
      toast.error('Failed to load GL mappings');
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [companiesRes, coaRes] = await Promise.all([
        companyAPI.getCompanyCodes(),
        financeAPI.getChartOfAccounts()
      ]);
      setCompanies(companiesRes.data || []);
      setChartOfAccounts(coaRes.data || []);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const loadGLAccounts = async (coaId) => {
    if (!coaId) {
      setGlAccounts([]);
      return;
    }
    try {
      const response = await financeAPI.getGLAccountsInCOA(coaId);
      setGlAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load GL accounts:', error);
      // If the API fails, try to get accounts from the COA object
      const coa = chartOfAccounts.find(c => c.coa_id === coaId);
      if (coa && coa.accounts) {
        setGlAccounts(coa.accounts);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'chart_of_accounts_id') {
      loadGLAccounts(value);
    }
  };

  const handleSwitchChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const resetForm = () => {
    setFormData({
      mapping_id: '',
      name: '',
      description: '',
      company_code_id: '',
      chart_of_accounts_id: '',
      wages_expense_account: '',
      overtime_expense_account: '',
      bonus_expense_account: '',
      allowance_expense_account: '',
      employer_ss_expense_account: '',
      tax_liability_account: '',
      ss_liability_account: '',
      net_pay_clearing_account: '',
      deductions_liability_account: '',
      payroll_accrual_account: '',
      use_cost_center: true,
      default_cost_center: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_to: '',
      status: 'Active'
    });
    setGlAccounts([]);
    setEditingItem(null);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    await loadGLAccounts(item.chart_of_accounts_id);
    setFormData({
      mapping_id: item.mapping_id,
      name: item.name || '',
      description: item.description || '',
      company_code_id: item.company_code_id || '',
      chart_of_accounts_id: item.chart_of_accounts_id || '',
      wages_expense_account: item.wages_expense_account || '',
      overtime_expense_account: item.overtime_expense_account || '',
      bonus_expense_account: item.bonus_expense_account || '',
      allowance_expense_account: item.allowance_expense_account || '',
      employer_ss_expense_account: item.employer_ss_expense_account || '',
      tax_liability_account: item.tax_liability_account || '',
      ss_liability_account: item.ss_liability_account || '',
      net_pay_clearing_account: item.net_pay_clearing_account || '',
      deductions_liability_account: item.deductions_liability_account || '',
      payroll_accrual_account: item.payroll_accrual_account || '',
      use_cost_center: item.use_cost_center ?? true,
      default_cost_center: item.default_cost_center || '',
      valid_from: item.valid_from || new Date().toISOString().split('T')[0],
      valid_to: item.valid_to || '',
      status: item.status || 'Active'
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        valid_to: formData.valid_to || null
      };
      
      if (editingItem) {
        await payrollAPI.updateGLMapping(editingItem.mapping_id, payload);
        toast.success('GL Mapping updated successfully');
      } else {
        await payrollAPI.createGLMapping(payload);
        toast.success('GL Mapping created successfully');
      }
      
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save GL mapping:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save GL mapping');
      }
    }
  };

  const filteredMappings = mappings.filter(m => 
    (m.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.mapping_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.company_code_id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderAccountSelect = (name, label, required = false, icon = null) => (
    <div className="space-y-1">
      <Label className="text-sm text-gray-600 flex items-center gap-1">
        {icon}
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <select
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        required={required}
        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        disabled={!formData.chart_of_accounts_id}
      >
        <option value="">Select Account</option>
        {glAccounts.map(acc => (
          <option key={acc.account_number} value={acc.account_number}>
            {acc.account_number} - {acc.account_name}
          </option>
        ))}
      </select>
    </div>
  );

  if (showForm) {
    return (
      <Layout>
        <div className="p-6" data-testid="gl-mapping-form">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => { setShowForm(false); resetForm(); }} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {editingItem ? 'Edit GL Mapping' : 'Create GL Mapping'}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-orange-600" />
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Mapping ID <span className="text-red-500">*</span></Label>
                  <Input
                    name="mapping_id"
                    value={formData.mapping_id}
                    onChange={handleInputChange}
                    placeholder="e.g., GL_MAP_001"
                    required
                    disabled={!!editingItem}
                    data-testid="mapping-id-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Name <span className="text-red-500">*</span></Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Standard Payroll GL Mapping"
                    required
                    data-testid="mapping-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Status</Label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md text-sm"
                    data-testid="status-select"
                  >
                    <option value="Active">Active</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>
                <div className="col-span-full space-y-1">
                  <Label className="text-sm text-gray-600">Description</Label>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description of this GL mapping"
                    data-testid="description-input"
                  />
                </div>
              </div>
            </Card>

            {/* Organization Assignment */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-green-600" />
                Organization Assignment
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Company Code <span className="text-red-500">*</span></Label>
                  <select
                    name="company_code_id"
                    value={formData.company_code_id}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md text-sm"
                    data-testid="company-code-select"
                  >
                    <option value="">Select Company Code</option>
                    {companies.map(c => (
                      <option key={c.company_code_id} value={c.company_code_id}>
                        {c.company_code_id} - {c.name || c.company_code_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Chart of Accounts <span className="text-red-500">*</span></Label>
                  <select
                    name="chart_of_accounts_id"
                    value={formData.chart_of_accounts_id}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md text-sm"
                    data-testid="coa-select"
                  >
                    <option value="">Select Chart of Accounts</option>
                    {chartOfAccounts.map(coa => (
                      <option key={coa.coa_id} value={coa.coa_id}>
                        {coa.coa_id} - {coa.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Valid From <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    name="valid_from"
                    value={formData.valid_from}
                    onChange={handleInputChange}
                    required
                    data-testid="valid-from-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Valid To</Label>
                  <Input
                    type="date"
                    name="valid_to"
                    value={formData.valid_to}
                    onChange={handleInputChange}
                    data-testid="valid-to-input"
                  />
                </div>
              </div>
            </Card>

            {/* Expense Accounts */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-orange-600" />
                Expense Accounts (Debit)
              </h2>
              {!formData.chart_of_accounts_id && (
                <p className="text-sm text-amber-600 mb-4">Select a Chart of Accounts to load GL accounts</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderAccountSelect('wages_expense_account', 'Wages Expense Account', true, <DollarSign className="w-3 h-3" />)}
                {renderAccountSelect('overtime_expense_account', 'Overtime Expense Account', false)}
                {renderAccountSelect('bonus_expense_account', 'Bonus Expense Account', false)}
                {renderAccountSelect('allowance_expense_account', 'Allowances Expense Account', false)}
                {renderAccountSelect('employer_ss_expense_account', 'Employer SS Expense Account', false)}
              </div>
            </Card>

            {/* Liability Accounts */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-red-600" />
                Liability Accounts (Credit)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderAccountSelect('tax_liability_account', 'Tax Liability Account', true, <CreditCard className="w-3 h-3" />)}
                {renderAccountSelect('ss_liability_account', 'Social Security Liability', true)}
                {renderAccountSelect('net_pay_clearing_account', 'Net Pay Clearing (Bank)', true, <Landmark className="w-3 h-3" />)}
                {renderAccountSelect('deductions_liability_account', 'Deductions Liability', false)}
                {renderAccountSelect('payroll_accrual_account', 'Payroll Accrual Account', false)}
              </div>
            </Card>

            {/* Cost Assignment */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-orange-600" />
                Cost Assignment
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.use_cost_center}
                    onCheckedChange={(checked) => handleSwitchChange('use_cost_center', checked)}
                    data-testid="use-cost-center-switch"
                  />
                  <Label className="text-sm text-gray-700">Post to Employee's Cost Center</Label>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm text-gray-600">Default Cost Center</Label>
                  <Input
                    name="default_cost_center"
                    value={formData.default_cost_center}
                    onChange={handleInputChange}
                    placeholder="Used when employee has no cost center"
                    data-testid="default-cost-center-input"
                  />
                </div>
              </div>
            </Card>

            {/* Form Actions */}
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setShowForm(false); resetForm(); }}
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="save-btn">
                {editingItem ? 'Update Mapping' : 'Create Mapping'}
              </Button>
            </div>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="gl-mapping-list">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">GL Account Mapping</h1>
            <p className="text-gray-600 mt-1">Configure payroll to finance GL account mappings</p>
          </div>
          {writable && (<Button 
            onClick={() => setShowForm(true)} 
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="create-mapping-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Mapping
          </Button>)}
        </div>

        {/* Search */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by ID, name, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="search-input"
            />
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredMappings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? 'No mappings found matching your search' : 'No GL mappings configured yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="mappings-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Mapping ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Chart of Accounts</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Valid From</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMappings.map((mapping) => (
                    <tr key={mapping.mapping_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{mapping.mapping_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{mapping.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {mapping.company_code_name || mapping.company_code_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {mapping.chart_of_accounts_name || mapping.chart_of_accounts_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{mapping.valid_from}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          mapping.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {mapping.status === 'Active' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {mapping.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <RecordActions doctype="glmapping" onView={() => setViewItem(mapping)} onEdit={() => handleEdit(mapping)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default GLMapping;
